import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adjustAppointmentPrice } from '../../api/appointments';
import { useToast } from '../../context/ToastContext';
import { getFriendlyError } from '../../utils/friendlyError';
import { calculateAdjustedPrice } from '../../utils/pricingCalculator';
import { adjustmentLabel, formatLkr, getAppointmentPricing } from '../../utils/pricingDisplay';

const ADJUSTABLE = ['APPROVED', 'ASSIGNED', 'IN_PROGRESS'];

const defaultAdjustmentType = (type) =>
  type && type !== 'NONE' ? type : 'DISCOUNT';

const AppointmentPriceAdjustment = ({ appointment, onUpdated }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { base, staffSurcharge, subtotal, final, hasAdjustment } = getAppointmentPricing(appointment);
  const p = appointment.pricing || {};

  const [adjustmentType, setAdjustmentType] = useState(defaultAdjustmentType(p.adjustmentType));
  const [adjustmentMode, setAdjustmentMode] = useState(p.adjustmentMode || 'FLAT');
  const [adjustmentValue, setAdjustmentValue] = useState(String(p.adjustmentValue ?? ''));
  const [adjustmentReason, setAdjustmentReason] = useState(p.adjustmentReason || '');

  useEffect(() => {
    setAdjustmentType(defaultAdjustmentType(p.adjustmentType));
    setAdjustmentMode(p.adjustmentMode || 'FLAT');
    setAdjustmentValue(p.adjustmentValue != null ? String(p.adjustmentValue) : '');
    setAdjustmentReason(p.adjustmentReason || '');
  }, [appointment._id, p.adjustmentType, p.adjustmentMode, p.adjustmentValue, p.adjustmentReason]);

  const previewFinal = (() => {
    try {
      return calculateAdjustedPrice(
        subtotal,
        adjustmentType,
        Number(adjustmentValue || 0),
        adjustmentType === 'MANUAL_OVERRIDE' ? 'FLAT' : adjustmentMode
      );
    } catch {
      return subtotal;
    }
  })();

  const adjustMut = useMutation({
    mutationFn: () =>
      adjustAppointmentPrice(appointment._id, {
        adjustmentType,
        adjustmentValue: Number(adjustmentValue || 0),
        adjustmentMode: adjustmentType === 'MANUAL_OVERRIDE' ? 'FLAT' : adjustmentMode,
        adjustmentReason: adjustmentReason.trim(),
      }),
    onSuccess: () => {
      showToast(t('toast.priceUpdated'), 'success');
      onUpdated();
    },
    onError: (err) => showToast(getFriendlyError(err, t('pricing.updatePriceFailed')), 'error'),
  });

  if (!ADJUSTABLE.includes(appointment.status)) return null;

  return (
    <div className="space-y-3 rounded-lg border border-zinc-200 dark:border-zinc-800 p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{t('pricing.adjustPrice')}</p>
        {hasAdjustment && (
          <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs text-amber-600 dark:text-amber-300">
            {t('pricing.priceAdjusted')}
          </span>
        )}
      </div>
      <p className="text-xs text-zinc-600 dark:text-zinc-500">{t('pricing.basePrice')}: {formatLkr(base)}</p>
      {staffSurcharge > 0 && (
        <p className="text-xs text-zinc-600 dark:text-zinc-500">
          {t('pricing.staffPremium')}: +{formatLkr(staffSurcharge)}
        </p>
      )}
      {(staffSurcharge > 0 || hasAdjustment) && (
        <p className="text-xs text-zinc-600 dark:text-zinc-500">{t('pricing.subtotal')}: {formatLkr(subtotal)}</p>
      )}

      <label className="block text-xs text-zinc-600 dark:text-zinc-400">
        {t('pricing.type')}
        <select
          value={adjustmentType}
          onChange={(e) => setAdjustmentType(e.target.value)}
          className="mt-1 w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-1.5 text-sm"
        >
          <option value="DISCOUNT">{t('pricing.discount')}</option>
          <option value="SURCHARGE">{t('pricing.surcharge')}</option>
          <option value="MANUAL_OVERRIDE">{t('pricing.manualOverride')}</option>
        </select>
      </label>

      {adjustmentType !== 'MANUAL_OVERRIDE' && (
        <label className="block text-xs text-zinc-600 dark:text-zinc-400">
          {t('pricing.mode')}
          <select
            value={adjustmentMode}
            onChange={(e) => setAdjustmentMode(e.target.value)}
            className="mt-1 w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-1.5 text-sm"
          >
            <option value="FLAT">{t('pricing.flatLkr')}</option>
            <option value="PERCENTAGE">{t('pricing.percentageSymbol')}</option>
          </select>
        </label>
      )}

      <label className="block text-xs text-zinc-600 dark:text-zinc-400">
        {adjustmentType === 'MANUAL_OVERRIDE' ? t('pricing.finalPriceLkr') : t('pricing.value')}
        <input
          type="number"
          min="0"
          step="0.01"
          value={adjustmentValue}
          onChange={(e) => setAdjustmentValue(e.target.value)}
          className="mt-1 w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-1.5 text-sm"
        />
      </label>

      <label className="block text-xs text-zinc-600 dark:text-zinc-400">
        {t('pricing.reasonOptionalShort')}
        <textarea
          value={adjustmentReason}
          onChange={(e) => setAdjustmentReason(e.target.value.slice(0, 500))}
          rows={2}
          className="mt-1 w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-1.5 text-sm"
        />
      </label>

      <p className="text-sm">
        {t('pricing.previewAmount', { amount: formatLkr(previewFinal) })}
        {previewFinal !== final && (
          <span className="ml-2 text-xs text-zinc-600 dark:text-zinc-500">
            {t('pricing.currentAmount', { amount: formatLkr(final) })}
          </span>
        )}
      </p>

      <button
        type="button"
        disabled={adjustMut.isPending || adjustmentValue === ''}
        onClick={() => adjustMut.mutate()}
        className="w-full rounded-lg bg-glow-600 py-2 text-sm text-white disabled:opacity-50"
      >
        {adjustMut.isPending ? t('common.saving') : t('pricing.applyAdjustment')}
      </button>
    </div>
  );
};

export const AppointmentPricingReceipt = ({ appointment, compact }) => {
  const { t } = useTranslation();
  const {
    base,
    staffSurcharge,
    subtotal,
    final,
    delta,
    hasAdjustment,
    hasStaffSurcharge,
    staffSurchargeStaffName,
  } = getAppointmentPricing(appointment);
  const p = appointment.pricing || {};
  if (!base && !final) return null;

  const label = adjustmentLabel(p.adjustmentType, p.adjustmentValue, p.adjustmentMode);
  const staffPremiumLabel = staffSurchargeStaffName
    ? t('pricing.staffPremiumNamed', { name: staffSurchargeStaffName })
    : t('pricing.staffPremium');

  if (compact) {
    if (!hasAdjustment && final === base) {
      return <p className="text-sm text-zinc-700 dark:text-zinc-300">{formatLkr(final)}</p>;
    }
    return (
      <div className="text-sm">
        {subtotal !== final && subtotal > base && <p className="text-zinc-500">{formatLkr(subtotal)}</p>}
        {base !== final && base === subtotal && <p className="text-zinc-500 line-through">{formatLkr(base)}</p>}
        <p className="font-medium text-glow-300">{formatLkr(final)}</p>
        {hasStaffSurcharge && (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            {staffPremiumLabel}: +{formatLkr(staffSurcharge)}
          </p>
        )}
        {label && delta !== 0 && <p className="text-xs text-emerald-600 dark:text-emerald-400">{label}</p>}
        {p.adjustmentReason && (
          <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-500">{p.adjustmentReason}</p>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-950/50 p-3 text-sm">
      <p className="font-medium text-zinc-700 dark:text-zinc-300">{t('pricing.title')}</p>
      <div className="mt-2 space-y-1">
        <div className="flex justify-between text-zinc-600 dark:text-zinc-400">
          <span>{t('pricing.basePrice')}</span>
          <span>{formatLkr(base)}</span>
        </div>
        {hasStaffSurcharge && (
          <div className="flex justify-between text-amber-600 dark:text-amber-400">
            <span>{staffPremiumLabel}</span>
            <span>+{formatLkr(staffSurcharge)}</span>
          </div>
        )}
        {p.adjustmentType && p.adjustmentType !== 'NONE' && delta !== 0 && (
          <div className={`flex justify-between ${delta < 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
            <span>{delta < 0 ? t('pricing.discount') : t('pricing.surcharge')}</span>
            <span>{delta < 0 ? '−' : '+'}{formatLkr(Math.abs(delta))}</span>
          </div>
        )}
        <div className="flex justify-between border-t border-zinc-200 dark:border-zinc-800 pt-2 font-semibold text-glow-300">
          <span>{t('pricing.final')}</span>
          <span>{formatLkr(final)}</span>
        </div>
      </div>
      {p.adjustmentReason && (
        <p className="mt-2 text-xs text-zinc-600 dark:text-zinc-500">{t('pricing.noteReason', { reason: p.adjustmentReason })}</p>
      )}
    </div>
  );
};

export default AppointmentPriceAdjustment;
